import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Checkbox,
  Skeleton,
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { Report } from '../../models/report';
import { excuseApi } from '../../commons/http';
import {
  financialStatusTranslations,
  getFinancialStatusColor,
} from '../../utils/statusTranslations';

const StatCard = ({ title, value, icon, color }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        {icon}
        <Typography variant="h6" component="div" sx={{ ml: 1 }}>
          {title}
        </Typography>
      </Box>
      <Typography variant="h4" color={color || 'primary'}>
        {value}
      </Typography>
    </CardContent>
  </Card>
);

const getDaysChipColor = (days) => {
  if (days <= 3) return 'error';
  if (days <= 7) return 'warning';
  return 'success';
};

const SubscriptionHealthDashboard = ({ setPageTitle }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [batchLoading, setBatchLoading] = useState(false);

  useEffect(() => {
    setPageTitle('Theo dõi gói dịch vụ');
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const result = await Report.fetch('subscription_health');
      setData(result);
      setSelectedIds([]);
    } catch (error) {
      toast.error('Không thể tải dữ liệu');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedIds(data.expired_active.map((item) => item.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleBatchComplete = async () => {
    if (!selectedIds.length) return;
    setBatchLoading(true);
    try {
      await excuseApi({
        path: '/user-packages/batch-complete',
        method: 'POST',
        body: { ids: selectedIds },
      });
      toast.success(`Đã hoàn thành ${selectedIds.length} gói dịch vụ`);
      fetchData();
    } catch (error) {
      toast.error('Không thể cập nhật. Vui lòng thử lại.');
    } finally {
      setBatchLoading(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 1 }} />
            </Grid>
          ))}
        </Grid>
        <Skeleton variant="rectangular" height={300} sx={{ mt: 4, borderRadius: 1 }} />
        <Skeleton variant="rectangular" height={300} sx={{ mt: 4, borderRadius: 1 }} />
      </Container>
    );
  }

  if (!data) return null;

  const { summary, expiring_soon, expired_active } = data;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Summary Cards */}
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Đang hoạt động"
            value={summary.truly_active}
            icon={<PeopleIcon color="success" sx={{ fontSize: 30 }} />}
            color="success.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Hết hạn chưa xử lý"
            value={summary.expired_active}
            icon={<WarningAmberIcon color="error" sx={{ fontSize: 30 }} />}
            color="error.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Hết hạn tuần này"
            value={summary.expiring_this_week}
            icon={<ScheduleIcon color="warning" sx={{ fontSize: 30 }} />}
            color="warning.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Hết hạn trong 30 ngày"
            value={summary.expiring_this_month}
            icon={<CalendarMonthIcon color="info" sx={{ fontSize: 30 }} />}
            color="info.main"
          />
        </Grid>
      </Grid>

      {/* Expiring Soon Table */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" gutterBottom>
          Sắp hết hạn (14 ngày tới) — {expiring_soon.length} gói
        </Typography>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Khách hàng</TableCell>
                <TableCell>SĐT</TableCell>
                <TableCell>Gói</TableCell>
                <TableCell>Ngày hết hạn</TableCell>
                <TableCell>Còn lại</TableCell>
                <TableCell>Thanh toán</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {expiring_soon.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    Không có gói nào sắp hết hạn
                  </TableCell>
                </TableRow>
              ) : (
                expiring_soon.map((item) => (
                  <TableRow
                    key={item.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/user-packages/${item.id}`)}
                  >
                    <TableCell>{item.user_name}</TableCell>
                    <TableCell>{item.user_phone}</TableCell>
                    <TableCell>{item.package_name}</TableCell>
                    <TableCell>{dayjs(item.ending_date).format('DD/MM/YYYY')}</TableCell>
                    <TableCell>
                      <Chip
                        label={`${item.days_until_expiry} ngày`}
                        color={getDaysChipColor(item.days_until_expiry)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={financialStatusTranslations[item.financial_status] || item.financial_status}
                        color={getFinancialStatusColor(item.financial_status)}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Expired But Active Table */}
      <Box sx={{ mt: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6">
            Hết hạn nhưng chưa xử lý — {expired_active.length} gói
          </Typography>
          {selectedIds.length > 0 && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleBatchComplete}
              disabled={batchLoading}
            >
              {batchLoading
                ? 'Đang xử lý...'
                : `Hoàn thành ${selectedIds.length} gói đã chọn`}
            </Button>
          )}
        </Box>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={
                      selectedIds.length > 0 && selectedIds.length < expired_active.length
                    }
                    checked={
                      expired_active.length > 0 && selectedIds.length === expired_active.length
                    }
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell>Khách hàng</TableCell>
                <TableCell>SĐT</TableCell>
                <TableCell>Gói</TableCell>
                <TableCell>Ngày hết hạn</TableCell>
                <TableCell>Quá hạn</TableCell>
                <TableCell>Thanh toán</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {expired_active.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    Không có gói nào hết hạn chưa xử lý
                  </TableCell>
                </TableRow>
              ) : (
                expired_active.map((item) => (
                  <TableRow key={item.id} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedIds.includes(item.id)}
                        onChange={() => handleSelectOne(item.id)}
                      />
                    </TableCell>
                    <TableCell
                      sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                      onClick={() => navigate(`/user-packages/${item.id}`)}
                    >
                      {item.user_name}
                    </TableCell>
                    <TableCell>{item.user_phone}</TableCell>
                    <TableCell>{item.package_name}</TableCell>
                    <TableCell>{dayjs(item.ending_date).format('DD/MM/YYYY')}</TableCell>
                    <TableCell>
                      <Chip
                        label={`${item.days_overdue} ngày`}
                        color="error"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={financialStatusTranslations[item.financial_status] || item.financial_status}
                        color={getFinancialStatusColor(item.financial_status)}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Container>
  );
};

export default SubscriptionHealthDashboard;
